import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";

import cameras from "../data/cameras";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import curatedCombos from "../data/recipes-curated.json";
import parameters from "../data/parameters.json";
import parameterPages from "../data/parameter-pages.json";
import Footer from "../components/Footer";
import SplitPanel from "../components/SplitPanel";
import { CAMERA_NEUTRAL_GRADIENT } from "../utils/lightingGradients";
import { usePublished } from "../utils/publishing";
import { setPageMeta, breadcrumbLd } from "../utils/meta";
import {
  frameRateSummary, resolutionSummary, profileSummary,
  apertureSummary, ndSummary, stabilizationSummary, coverageNote,
} from "../utils/cameraFacts";




export default function CameraHub() {
  const { cameraId } = useParams();
  const camera = cameras.find((c) => c.id === cameraId);

  const { items: live } = usePublished(curatedCombos);
  const { items: liveParams } = usePublished(parameterPages);

  // The topic cluster: one link per technical reference page that exists
  // for this camera. These are what connect a hub to its long-tail pages,
  // and they appear as the schedule reveals them.
  const topicsForCamera = useMemo(
    () =>
      liveParams
        .filter((p) => p.cameraId === cameraId)
        .map((p) => ({ ...p, def: parameters.find((d) => d.id === p.parameterId) }))
        .filter((p) => p.def),
    [liveParams, cameraId]
  );

  const combosForCamera = useMemo(
    () => live.filter((c) => c.cameraId === cameraId),
    [live, cameraId]
  );

  const groupedByCategory = useMemo(() => {
    const byCategory = {};
    combosForCamera.forEach((combo) => {
      const scenario = scenarios.find((s) => s.id === combo.scenarioId);
      const category = scenario?.category || "Other";
      if (!byCategory[category]) byCategory[category] = [];
      byCategory[category].push(combo);
    });
    return byCategory;
  }, [combosForCamera]);

  useEffect(() => {
    if (!camera) return;
    const title = `${camera.name} Settings: Complete Guide & Shot Recipes`;
    const description = `${camera.name} settings by scenario and lighting: frame rate, shutter, ISO, white balance, ND filter and picture profile, from confirmed specs.`;
    const path = `/cameras/${cameraId}/`;

    setPageMeta({
      title,
      description,
      path,
      jsonLdId: "camerahub-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "CollectionPage", name: title, description, url: `${window.location.origin}${path}` },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: camera.name, path }]),
        ],
      },
    });
  }, [camera, cameraId]);

  if (!camera) {
    return (
      <div style={{ maxWidth: 600, margin: "60px auto", padding: "0 20px", fontFamily: "sans-serif" }}>
        <h1>Camera not found</h1>
        <Link to="/">Back to all cameras</Link>
      </div>
    );
  }

  return (
    <>
    <SplitPanel
      gradient={CAMERA_NEUTRAL_GRADIENT}
      imageText={{ eyebrow: camera.manufacturer, title: camera.name, sub: "Settings & recipes" }}
    >
    <main className="camera-hub split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>{camera.name}</span>
      </nav>

      <h1>{camera.name} Settings</h1>
      <p className="hub-dek">
        The complete guide and recipe index for the {camera.name}. Every combination below is calculated
        from this camera's confirmed specs, not a generic template.
      </p>

      <section className="hub-specs" aria-labelledby="specs-heading">
        <h2 id="specs-heading">Confirmed capabilities</h2>
        <ul>
          <li>Frame rates: {frameRateSummary(camera)}</li>
          <li>Resolutions: {resolutionSummary(camera)}</li>
          <li>Aperture: {apertureSummary(camera)}</li>
          <li>Picture profiles: {profileSummary(camera)}</li>
          <li>
            ND filter: {ndSummary(camera)}
            {camera.capabilities.ndNote ? `, ${camera.capabilities.ndNote}` : ""}
          </li>
          <li>
            Stabilization: {stabilizationSummary(camera)}
            {camera.capabilities.stabilizationNote ? `, ${camera.capabilities.stabilizationNote}` : ""}
          </li>
        </ul>
        {coverageNote(camera) && <p className="hub-coverage-note">{coverageNote(camera)}</p>}
      </section>

      {topicsForCamera.length > 0 && (
        <section className="hub-category" aria-labelledby="topics-heading">
          <h2 id="topics-heading">{camera.name} Settings Reference</h2>
          <ul className="hub-recipe-list">
            {topicsForCamera.map((t) => (
              <li key={t.parameterId}>
                <Link to={`/cameras/${camera.id}/${t.parameterId}`}>
                  {camera.name} {t.def.name.toLowerCase()} settings
                </Link>
                <span className="hub-topic-summary">, {t.def.summary}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {Object.keys(groupedByCategory).length === 0 && (
        <p className="hub-empty">No published recipes for this camera yet.</p>
      )}

      {Object.entries(groupedByCategory).map(([category, combos]) => (
        <section key={category} className="hub-category" aria-labelledby={`cat-${category}`}>
          <h2 id={`cat-${category}`}>Best {category} Settings</h2>
          <ul className="hub-recipe-list">
            {combos.map((combo) => {
              const scenario = scenarios.find((s) => s.id === combo.scenarioId);
              const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
              return (
                <li key={combo.slug}>
                  <Link to={`/camera/${combo.slug}`}>
                    {scenario.name} in {lighting.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {/* Marker element read by scripts/prerender.js (Puppeteer). */}
      <div data-prerender-ready="true" style={{ display: "none" }} />

    </main>
    </SplitPanel>
    <Footer />
    </>
  );
}
